import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import {
  FaFileInvoiceDollar,
  FaSearch,
  FaCheckCircle,
  FaMoneyBillWave,
  FaCreditCard,
  FaReceipt,
  FaBoxOpen,
  FaUndoAlt,
  FaHistory
} from "react-icons/fa";

export default function InvoiceView() {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("all");

  useEffect(() => {
    document.title = "Show Sales — Admin";
    fetchInvoices();
  }, []);

  const fetchInvoices = () => {
    setLoading(true);
    axios
      .get(`/api/fetch-invoice`)
      .then((res) => {
        if (res.data.status === 200) {
          setInvoices(res.data.data || []);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getValidMoment = (date1, date2) => {
    if (date1 && typeof date1 === "string" && !date1.startsWith("0000") && moment(date1).isValid()) return moment(date1);
    if (date2 && typeof date2 === "string" && !date2.startsWith("0000") && moment(date2).isValid()) return moment(date2);
    return null;
  };

  // Sort by latest completed date
  const sortedInvoices = [...invoices].sort((a, b) => {
    const ma = getValidMoment(a.order_complete, a.created_at);
    const mb = getValidMoment(b.order_complete, b.created_at);
    const da = ma ? ma.valueOf() : 0;
    const db = mb ? mb.valueOf() : 0;
    return db - da;
  });

  // Filtered dataset
  const filteredList = sortedInvoices.filter((item) => {
    const term = searchTerm.toLowerCase();
    const customerName = `${item.firstname || ""} ${item.lastname || ""}`.toLowerCase();
    const tracking = (item.tracking_no || "").toLowerCase();
    const email = (item.email || "").toLowerCase();
    const phone = (item.phone || "").toLowerCase();

    const matchesSearch =
      !searchTerm ||
      customerName.includes(term) ||
      tracking.includes(term) ||
      email.includes(term) ||
      phone.includes(term);

    const matchesPayment =
      paymentFilter === "all" ||
      (item.payment_mode && item.payment_mode.toLowerCase() === paymentFilter.toLowerCase());

    return matchesSearch && matchesPayment;
  });

  const codCount = invoices.filter((i) => (i.payment_mode || "").toUpperCase() === "COD").length;
  const bankCount = invoices.filter((i) => (i.payment_mode || "").toUpperCase() === "BT").length;

  if (loading) {
    return (
      <div className="py-5 text-center">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="text-muted mt-2 fw-semibold">Loading Sales & Invoices Record...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid px-0">
      {/* Page Header */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <div className="d-flex align-items-center gap-2">
            <h3 className="fw-bold mb-0 text-dark" style={{ letterSpacing: "-0.5px" }}>
              Show Sales
            </h3>
            <span className="badge rounded-pill bg-success bg-opacity-10 text-success fw-semibold px-2.5 py-1">
              Finalized Invoices
            </span>
          </div>
          <p className="text-muted small mb-0 mt-1">
            Authoritative log of fulfilled orders, commercial sales invoices, and customer payment settlements.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Link
            to="/admin/orders"
            className="btn btn-outline-secondary d-inline-flex align-items-center gap-2 fw-semibold px-3 py-2 rounded-3"
          >
            <FaBoxOpen /> Order Manager
          </Link>
          <Link
            to="/admin/refundView"
            className="btn btn-outline-danger d-inline-flex align-items-center gap-2 fw-semibold px-3 py-2 rounded-3"
          >
            <FaUndoAlt /> Refunds
          </Link>
        </div>
      </div>

      {/* KPI Stats Row */}
      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="admin-stat-card">
            <div className="admin-stat-header">
              <span className="admin-stat-label">Total Completed Sales</span>
              <div className="admin-stat-icon" style={{ background: "#dcfce7", color: "#16a34a" }}>
                <FaReceipt />
              </div>
            </div>
            <div className="admin-stat-value text-success">{invoices.length}</div>
            <div className="admin-stat-footer text-muted">
              <FaCheckCircle className="text-success me-1" /> Recognized commercial transactions
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="admin-stat-card">
            <div className="admin-stat-header">
              <span className="admin-stat-label">Cash on Delivery (COD)</span>
              <div className="admin-stat-icon" style={{ background: "#fef3c7", color: "#d97706" }}>
                <FaMoneyBillWave />
              </div>
            </div>
            <div className="admin-stat-value text-dark">{codCount}</div>
            <div className="admin-stat-footer text-muted">
              Settled via logistics courier partners
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="admin-stat-card">
            <div className="admin-stat-header">
              <span className="admin-stat-label">Bank transfer</span>
              <div className="admin-stat-icon" style={{ background: "#e0f2fe", color: "#0284c7" }}>
                <FaCreditCard />
              </div>
            </div>
            <div className="admin-stat-value text-primary">{bankCount}</div>
            <div className="admin-stat-footer text-muted">
              Waiting for the customer transfer to clear
            </div>
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="admin-card">
        {/* Filters and Search Bar */}
        <div className="admin-card-header bg-white d-flex flex-wrap align-items-center justify-content-between gap-3 py-3">
          <div className="d-flex align-items-center gap-2">
            <button
              onClick={() => setPaymentFilter("all")}
              className={`btn btn-sm rounded-pill px-3 fw-semibold ${
                paymentFilter === "all" ? "btn-dark" : "btn-light text-muted"
              }`}
            >
              All Sales ({invoices.length})
            </button>
            <button
              onClick={() => setPaymentFilter("cod")}
              className={`btn btn-sm rounded-pill px-3 fw-semibold ${
                paymentFilter === "cod" ? "btn-dark" : "btn-light text-muted"
              }`}
            >
              COD ({codCount})
            </button>
            <button
              onClick={() => setPaymentFilter("BT")}
              className={`btn btn-sm rounded-pill px-3 fw-semibold ${
                paymentFilter === "BT" ? "btn-dark" : "btn-light text-muted"
              }`}
            >
              Bank transfer ({bankCount})
            </button>
          </div>

          <div className="position-relative" style={{ minWidth: "280px" }}>
            <FaSearch
              className="position-absolute text-muted"
              style={{ top: "11px", left: "12px", fontSize: "14px" }}
            />
            <input
              type="search"
              className="form-control form-control-sm ps-5 rounded-pill"
              placeholder="Search by customer, tracking, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Sales Table */}
        <div className="table-responsive">
          {filteredList.length === 0 ? (
            <div className="p-5 text-center text-muted">
              <FaFileInvoiceDollar size={36} className="text-secondary opacity-50 mb-3" />
              <h6 className="fw-bold">No sales records found</h6>
              <p className="small mb-3">Try adjusting your search criteria or payment mode filter.</p>
              {(searchTerm || paymentFilter !== "all") && (
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setPaymentFilter("all");
                  }}
                  className="btn btn-sm btn-outline-primary rounded-pill px-3"
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <table className="table align-middle mb-0">
              <thead className="table-light text-muted small text-uppercase" style={{ letterSpacing: "0.5px" }}>
                <tr>
                  <th style={{ width: "50px" }}>#</th>
                  <th>Tracking / Order</th>
                  <th>Customer Information</th>
                  <th>Payment Mode</th>
                  <th>Completed At</th>
                  <th>Sale Created By</th>
                  <th className="text-end" style={{ width: "150px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredList.map((item, index) => {
                  const customerName = `${item.firstname || ""} ${item.lastname || ""}`.trim() || "Customer";
                  const initial = customerName.charAt(0).toUpperCase();
                  const isCod = (item.payment_mode || "").toUpperCase() === "COD";

                  return (
                    <tr key={index}>
                      <td className="text-muted fw-semibold">{index + 1}</td>
                      <td>
                        <div className="fw-bold text-dark font-monospace">
                          {item.tracking_no || `ORD-#${item.order_id}`}
                        </div>
                        <span className="text-muted small">Order #{item.order_id}</span>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded-circle bg-primary bg-opacity-10 text-primary d-flex align-items-center justify-content-center fw-bold"
                            style={{ width: "32px", height: "32px", fontSize: "13px" }}
                          >
                            {initial}
                          </div>
                          <div>
                            <div className="fw-semibold text-dark">{customerName}</div>
                            <div className="text-muted small d-flex align-items-center gap-2">
                              {item.email && <span>{item.email}</span>}
                              {item.phone && <span>• {item.phone}</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span
                          className={`badge rounded-pill px-2.5 py-1.5 fw-semibold d-inline-flex align-items-center gap-1 ${
                            isCod
                              ? "bg-warning bg-opacity-10 text-warning text-dark border border-warning"
                              : "bg-info bg-opacity-10 text-primary border border-info"
                          }`}
                          style={{ fontSize: "12px" }}
                        >
                          {isCod ? <FaMoneyBillWave size={11} /> : <FaCreditCard size={11} />}
                          {isCod ? "Cash on Delivery" : (item.payment_mode || "").toUpperCase() === "BT" ? "Bank transfer" : (item.payment_mode || "Cash on Delivery")}
                        </span>
                      </td>
                      <td>
                        <div className="fw-medium text-dark">
                          {getValidMoment(item.order_complete, item.created_at)?.format("DD MMM YYYY") || "—"}
                        </div>
                        <div className="text-muted small">
                          {getValidMoment(item.order_complete, item.created_at)?.format("hh:mm A") || "—"}
                        </div>
                      </td>
                      <td>
                        <div className="fw-semibold small text-dark">{item.created_by || "System Admin"}</div>
                        {item.completed_email && (
                          <div className="text-muted small" style={{ fontSize: "11px" }}>
                            {item.completed_email}
                          </div>
                        )}
                      </td>
                      <td className="text-end">
                        <div className="d-flex align-items-center justify-content-end gap-1">
                          <Link
                            to={`/admin/invoice-details/${item.order_id}`}
                            className="btn btn-sm btn-primary fw-semibold d-inline-flex align-items-center gap-1 rounded-3 px-2.5 py-1 shadow-sm"
                            title="Check Invoice & Order History"
                          >
                            <FaFileInvoiceDollar size={12} /> Invoice
                          </Link>
                          <Link
                            to={`/admin/orders/${item.order_id}`}
                            className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1 rounded-3 px-2 py-1"
                            title="Order Details & Timeline"
                          >
                            <FaHistory size={11} /> History
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
